import { api } from "./client";
import type {
  LeadListItem,
  LeadListResponse,
  Activity,
  SublineStats,
  Lead,
  GoogleContactDeleteResult,
} from "./types";

export type LeadFilters = {
  saved_view?: string;
  pipeline_id?: number;
  stage_id?: number;
  owner_id?: number;
  type_id?: number;
  search?: string;
  archived?: boolean;
  cursor?: string;
  limit?: number;
};

export async function getLeads(filters: LeadFilters = {}): Promise<LeadListResponse> {
  const r = await api.get<LeadListResponse>("/api/v1/leads", { params: filters });
  return r.data;
}

export async function getLead(id: number): Promise<LeadListItem> {
  const r = await api.get<LeadListItem>(`/api/v1/leads/${id}`);
  return r.data;
}

export async function getLeadActivities(id: number): Promise<Activity[]> {
  const r = await api.get<Activity[]>(`/api/v1/leads/${id}/activities`);
  return r.data;
}

export type CreateLeadInput = {
  title: string;
  description?: string;
  owner_id: number;
  pipeline_id: number;
  stage_id: number;
  source_id?: number | null;
  type_id?: number | null;
  person_id?: number | null;
  lead_value?: number | null;
  monthly_income?: number | null;
  expected_close_date?: string | null;
  segment?: string | null;
  priority_stars?: number | null;
};

export async function createLead(input: CreateLeadInput): Promise<LeadListItem> {
  const r = await api.post<LeadListItem>("/api/v1/leads", input);
  return r.data;
}

/**
 * Jeden punkt dla obu mutacji zmiany leada (patchLead/changeLeadStage — pokrywa wszystkie
 * ścieżki UI zmiany etapu). Event, NIE bezpośrednie wołanie Pinia store — ten sam wzorzec
 * decoupling co auth:logout/api:rate-limit w api/client.ts (nasłuch w App.vue).
 */
function maybeNotifyGoogleContactPrompt(lead: LeadListItem): void {
  if (lead.google_contact_prompt) {
    window.dispatchEvent(new CustomEvent("lead:google-contact-prompt", { detail: { leadId: lead.id } }));
  }
}

// Łańcuch kart współpracownika — komunikat z linkiem pokazuje App.vue (ten sam wzorzec eventu).
function maybeNotifyChain(lead: LeadListItem): void {
  if (lead.chain) {
    window.dispatchEvent(new CustomEvent("lead:chain", { detail: lead.chain }));
  }
}

export async function patchLead(id: number, patch: Partial<Lead>): Promise<LeadListItem> {
  const r = await api.patch<LeadListItem>(`/api/v1/leads/${id}`, patch);
  maybeNotifyGoogleContactPrompt(r.data);
  maybeNotifyChain(r.data);
  return r.data;
}

export async function changeLeadStage(id: number, stage_id: number): Promise<LeadListItem> {
  // Backend Zod schema expects `stage_id` (post-pivot 2026-05-28 — stare nazwy pól porzucone).
  // Wysyłamy raw, omijając typed Partial<Lead> żeby uniknąć type clash.
  const r = await api.patch<LeadListItem>(`/api/v1/leads/${id}`, { stage_id });
  maybeNotifyGoogleContactPrompt(r.data);
  maybeNotifyChain(r.data);
  return r.data;
}

export async function archiveLead(id: number): Promise<void> {
  await api.delete(`/api/v1/leads/${id}`);
}

/**
 * Usunięcie kontaktu Google po lost (dialog HITL — docs/integrations/pilot-contacts.md §4).
 * Non-2xx (409 stan się zmienił / 503 integracja wyłączona / 502 Pilot nieosiągalny) rzuca
 * przez axios — dialog (F-2) łapie i mapuje na komunikat wg statusu HTTP.
 */
export async function deleteLeadGoogleContact(id: number): Promise<GoogleContactDeleteResult> {
  const r = await api.post<GoogleContactDeleteResult>(`/api/v1/leads/${id}/google-contact/delete`, { confirm: true });
  return r.data;
}

export async function restoreLead(id: number): Promise<LeadListItem> {
  return patchLead(id, { archived_at: null as unknown as Lead["archived_at"] });
}

export async function getSublineStats(filters: LeadFilters = {}): Promise<SublineStats> {
  const r = await api.get<SublineStats>("/api/v1/leads/subline-stats", { params: filters });
  return r.data;
}

export type CreateActivityInput = {
  type: "note" | "call" | "meeting" | "file"; // email → sendLeadEmail (zakładka Email wysyła)
  title: string;
  comment?: string;
  schedule_from?: string;
  schedule_to?: string;
  // v1.1 (C1) — łącze spotkania (tranzytowe, nie w DB): Meet/Zoom albo adres „stacjonarnie".
  conference?: "meet" | "zoom" | null;
  location?: string | null;
};

export async function createActivity(leadId: number, input: CreateActivityInput): Promise<Activity> {
  const r = await api.post<Activity>(`/api/v1/leads/${leadId}/activities`, input);
  return r.data;
}

export type LeadEmailInput = { subject: string; body_html: string; send_key: string };
export type LeadEmailResult = { sent: boolean; to: string; activity_id: number | null };

/**
 * Zwykły mail do klienta z karty szansy — wychodzi ze skrzynki OVB opiekuna przez Pilota, wpis
 * w historii dopisuje serwer po udanej wysyłce. `send_key` raz na jedno napisanie maila
 * (ponowienie nie wyśle drugiej kopii). Błędy jak przy mailu spotkania: `{ error, reason }`.
 */
export async function sendLeadEmail(leadId: number, input: LeadEmailInput): Promise<LeadEmailResult> {
  const r = await api.post<{ data: LeadEmailResult }>(`/api/v1/leads/${leadId}/email`, input);
  return r.data.data;
}

export async function patchActivity(id: number, patch: Partial<Activity>): Promise<Activity> {
  const r = await api.patch<Activity>(`/api/v1/activities/${id}`, patch);
  return r.data;
}

export async function deleteActivity(id: number): Promise<void> {
  await api.delete(`/api/v1/activities/${id}`);
}

export type AkademiaSyncStatus = "pending" | "retry" | "error" | "created" | "moved" | "exists" | "archived";

/** Wynik ostatniego zgłoszenia karty do Akademii (stan z historii karty, etykieta po polsku z serwera). */
export interface AkademiaSyncDto {
  status: AkademiaSyncStatus;
  label: string;
  message: string | null;
  updated_at: string;
  next_at: string | null;
}

export interface AkademiaAccessState {
  enabled: boolean;
  /** `candidate` = karta Rekrutacji, `team` = Wdrożenie albo Zespół, `null` = inny proces. */
  kind: "candidate" | "team" | null;
  sync: AkademiaSyncDto | null;
}

/** Stan zgłoszenia karty do Akademii (docs/integrations/akademia-kandydaci-zespol.md). */
export async function getAkademiaAccess(id: number): Promise<AkademiaAccessState> {
  const r = await api.get<{ data: AkademiaAccessState }>(`/api/v1/leads/${id}/akademia-access`);
  return r.data.data;
}

/** Ręczne ponowienie zgłoszenia do Akademii; błąd niesie `error` po polsku. */
export async function retryAkademiaAccess(id: number): Promise<{ kind: "candidate" | "team"; sync: AkademiaSyncDto }> {
  const r = await api.post<{ data: { kind: "candidate" | "team"; sync: AkademiaSyncDto } }>(`/api/v1/leads/${id}/akademia-access`);
  return r.data.data;
}
